// Estimated delivery date under the PDP free-shipping bar.
//
// Rendered by snippets/pdp-free-shipping-bar.liquid as [data-pdp-eta] with
// the rules as data attributes (section settings). Everything is computed
// here, in the browser, on purpose: Shopify caches the page HTML, so a date
// rendered by Liquid would go stale, and the "order in Xh Ym" countdown has
// to tick anyway.
//
// All clock reads are in Europe/London (the warehouse's day), never the
// visitor's local time — a shopper in New York at 9pm is ordering at 2am UK
// time and must see the next working day's estimate.
//
// Rules (fulfilment team, 2026-09-07):
//   Standard  dispatch Mon–Fri before 13:00, Royal Mail delivers Mon–Sat,
//             arrives 2–3 delivery days after dispatch.
//   Next Day  dispatch Mon–Fri before 13:00, arrives the next delivery day,
//             Saturday included (Friday before 13:00 lands Saturday).
// Both skip UK bank holidays for dispatch and delivery. Whichever service
// arrives first is shown; on a tie Next Day wins because it's a firm date
// rather than the start of a range. (With a 2-day Standard minimum, Next Day
// always wins in practice; the comparison is kept so the settings can't
// produce a slower "fastest" estimate.)

const TIME_ZONE = 'Europe/London';
const MS_PER_DAY = 86400000;

// Civil date in London as a UTC-midnight timestamp, so day arithmetic is
// immune to DST. Returns { day (timestamp), weekday 0=Sun..6=Sat, minutes }.
const londonNow = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(new Date());
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  const day = Date.UTC(get('year'), get('month') - 1, get('day'));
  // Some engines report midnight as 24.
  const hour = get('hour') % 24;
  return { day, weekday: new Date(day).getUTCDay(), minutes: hour * 60 + get('minute') };
};

const parseDayList = (value, fallback) => {
  const days = (value || '')
    .split(',')
    .map((d) => parseInt(d, 10))
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
  return days.length ? new Set(days) : new Set(fallback);
};

const parseHolidays = (value) => {
  const set = new Set();
  (value || '').split(/[\s,]+/).forEach((token) => {
    const m = token.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) set.add(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  });
  return set;
};

const parseCutoff = (value, fallbackMinutes) => {
  const m = (value || '').match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallbackMinutes;
  const minutes = +m[1] * 60 + +m[2];
  return minutes >= 0 && minutes < 1440 ? minutes : fallbackMinutes;
};

const isAllowed = (day, allowedWeekdays, holidays) =>
  allowedWeekdays.has(new Date(day).getUTCDay()) && !holidays.has(day);

const nextAllowed = (fromDay, allowedWeekdays, holidays, includeFrom) => {
  let day = includeFrom ? fromDay : fromDay + MS_PER_DAY;
  // 21-day ceiling guards against an empty/contradictory config looping forever.
  for (let i = 0; i < 21; i += 1, day += MS_PER_DAY) {
    if (isAllowed(day, allowedWeekdays, holidays)) return day;
  }
  return null;
};

const addDeliveryDays = (fromDay, count, allowedWeekdays, holidays) => {
  let day = fromDay;
  for (let i = 0; i < count; i += 1) {
    day = nextAllowed(day, allowedWeekdays, holidays, false);
    if (day === null) return null;
  }
  return day;
};

// One service: when would an order placed now be dispatched and delivered?
const estimate = (now, service, holidays) => {
  const dispatchesToday = isAllowed(now.day, service.dispatchDays, holidays) && now.minutes < service.cutoff;
  const dispatch = dispatchesToday
    ? now.day
    : nextAllowed(now.day, service.dispatchDays, holidays, false);
  if (dispatch === null) return null;
  const from = addDeliveryDays(dispatch, service.minDays, service.deliveryDays, holidays);
  const to = addDeliveryDays(dispatch, service.maxDays, service.deliveryDays, holidays);
  if (from === null || to === null) return null;
  return {
    dispatchesToday,
    minutesToCutoff: dispatchesToday ? service.cutoff - now.minutes : null,
    from,
    to,
  };
};

// Hand-rolled rather than Intl so September reads "Sep", not the ICU "Sept".
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDay = (day) => {
  const date = new Date(day);
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()}`;
};
const formatDate = (day) => `${formatDay(day)} ${MONTHS[new Date(day).getUTCMonth()]}`;
// "Mon 14 – Wed 16 Sep", or "Wed 30 Sep – Fri 2 Oct" across a month boundary.
const formatRange = (from, to) => {
  if (from === to) return formatDate(from);
  const sameMonth = new Date(from).getUTCMonth() === new Date(to).getUTCMonth();
  return `${sameMonth ? formatDay(from) : formatDate(from)} – ${formatDate(to)}`;
};
const formatCountdown = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

const fill = (template, values) =>
  Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), template || '');

const deliveryEstimate = () => {
  const root = document.querySelector('[data-pdp-eta]');
  if (!root) return;
  const mainEl = root.querySelector('[data-pdp-eta-main]');
  const subEl = root.querySelector('[data-pdp-eta-sub]');
  if (!mainEl) return;

  const d = root.dataset;
  const holidays = parseHolidays(d.holidays);
  const standard = {
    cutoff: parseCutoff(d.stdCutoff, 13 * 60),
    minDays: Math.max(1, parseInt(d.stdMin, 10) || 2),
    maxDays: Math.max(1, parseInt(d.stdMax, 10) || 3),
    dispatchDays: parseDayList(d.stdDispatchDays, [1, 2, 3, 4, 5]),
    deliveryDays: parseDayList(d.stdDeliveryDays, [1, 2, 3, 4, 5, 6]),
  };
  if (standard.maxDays < standard.minDays) standard.maxDays = standard.minDays;
  const nextDay = d.ndEnabled === 'true'
    ? {
      cutoff: parseCutoff(d.ndCutoff, 13 * 60),
      minDays: 1,
      maxDays: 1,
      dispatchDays: parseDayList(d.ndDispatchDays, [1, 2, 3, 4, 5]),
      deliveryDays: parseDayList(d.ndDeliveryDays, [1, 2, 3, 4, 5, 6]),
    }
    : null;

  const render = () => {
    const now = londonNow();
    const std = estimate(now, standard, holidays);
    const nd = nextDay ? estimate(now, nextDay, holidays) : null;
    if (!std) {
      root.hidden = true;
      return;
    }

    // Fastest wins; Next Day takes a tie (firm date beats the start of a range).
    const useNextDay = nd && nd.from <= std.from;
    const tomorrow = now.day + MS_PER_DAY;
    let main;
    let sub = '';

    if (useNextDay) {
      const values = {
        countdown: nd.minutesToCutoff !== null ? formatCountdown(nd.minutesToCutoff) : '',
        date: formatDate(nd.from),
        date_from: formatDate(std.from),
        date_to: formatDate(std.to),
        date_range: formatRange(std.from, std.to),
      };
      main = nd.dispatchesToday && nd.from === tomorrow
        ? fill(d.msgNdCountdown, values)
        : fill(d.msgNd, values);
      sub = fill(d.msgNdSub, values);
    } else {
      const values = {
        countdown: std.minutesToCutoff !== null ? formatCountdown(std.minutesToCutoff) : '',
        date: formatDate(std.from),
        date_from: formatDate(std.from),
        date_to: formatDate(std.to),
        date_range: formatRange(std.from, std.to),
      };
      main = std.dispatchesToday ? fill(d.msgStdCountdown, values) : fill(d.msgStd, values);
    }

    mainEl.textContent = main;
    if (subEl) {
      subEl.textContent = sub;
      subEl.hidden = !sub;
    }
    root.classList.toggle('is-next-day', Boolean(useNextDay));
    root.hidden = !main;
  };

  render();
  // Tick on the minute so the countdown and any cut-off flip stay exact.
  const msToNextMinute = 60000 - (Date.now() % 60000);
  setTimeout(() => {
    render();
    setInterval(render, 60000);
  }, msToNextMinute);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) render();
  });
};

export default deliveryEstimate;
